import { useQuery } from "@tanstack/react-query";
import type { EngineeringHealthArtifactsPage, EngineeringHealthMetricId, EngineeringHealthResponse, EngineeringHealthSection } from "@insights/contracts";
import { api } from "../../../api";

export type HealthFilters = { period: 30 | 90 | 180; repository: string; teamId: string; contributor: string };

export function normalizeHealthFilters(value: Partial<HealthFilters>): HealthFilters {
  const period = value.period === 90 || value.period === 180 ? value.period : 30;
  return {
    period,
    repository: value.repository ?? "",
    teamId: value.teamId ?? "",
    contributor: value.contributor ?? "",
  };
}

function queryString(section: EngineeringHealthSection, filters: HealthFilters) {
  const query = new URLSearchParams({ section, period: String(filters.period) });
  if (filters.repository) query.set("repository", filters.repository);
  if (filters.teamId) query.set("teamId", filters.teamId);
  if (filters.contributor) query.set("contributor", filters.contributor);
  return query;
}

export function useEngineeringHealth(workspaceId: string | undefined, section: EngineeringHealthSection, filters: HealthFilters) {
  return useQuery({
    enabled: Boolean(workspaceId),
    queryKey: ["engineering-health", workspaceId, section, filters],
    queryFn: () => api<EngineeringHealthResponse>(`/${workspaceId}/engineering-health?${queryString(section, filters)}`),
  });
}

export function useEngineeringHealthArtifacts(workspaceId: string | undefined, section: EngineeringHealthSection, filters: HealthFilters, metric: EngineeringHealthMetricId | null) {
  return useQuery({
    enabled: Boolean(workspaceId && metric),
    queryKey: ["engineering-health-artifacts", workspaceId, section, filters, metric],
    queryFn: () => api<EngineeringHealthArtifactsPage>(`/${workspaceId}/engineering-health/artifacts?${queryString(section, filters)}&metric=${metric}`),
  });
}
